import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { ABILITIES, AppAbility } from '@/lib/casl'
import { Dto, PublicDto } from '@/types'
import { AvailableCountry } from '@/types/openapi-public'

export type AppState = {
  user?: Dto['UserDetailsDto']
  resellers?: PublicDto['Reseller'][]
  isAuthenticated: boolean
  ability: AppAbility
  isLoading: boolean
  isRefreshingToken: boolean
  sessionId?: string
  idToken?: string
  accessToken?: string
  refreshToken?: string
  tokenExpiresAt?: number
  forceCountry?: AvailableCountry
  actions: {
    setIsAuthenticated: (isAuthenticated: boolean) => void
    setUser: (user: Dto['UserDetailsDto']) => void
    setResellers: (resellers: PublicDto['Reseller'][]) => void
    setAbility: (ability: AppAbility) => void
    setSessionId: (sessionId: string) => void
    clearSessionId: () => void
    setTokens: (tokens: { idToken: string; accessToken: string; refreshToken: string; expiresIn: number }) => void
    clearTokens: () => void
    setIsRefreshingToken: (isRefreshing: boolean) => void
    setForceCountry: (country?: AvailableCountry) => void
  }
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      user: undefined,
      resellers: undefined,
      isLoading: false,
      isAuthenticated: false,
      isRefreshingToken: false,
      ability: ABILITIES.DEFAULT,
      sessionId: undefined,
      idToken: undefined,
      accessToken: undefined,
      refreshToken: undefined,
      tokenExpiresAt: undefined,
      actions: {
        setIsAuthenticated: (val) => set({ isAuthenticated: val }),
        setUser: (user) => set({ user, ability: ABILITIES[user.role as keyof typeof ABILITIES] || ABILITIES.DEFAULT }),
        setResellers: (resellers) => set({ resellers }),
        setAbility: (ability) => set({ ability }),
        setSessionId: (sessionId) => set({ sessionId }),
        clearSessionId: () => set({ sessionId: undefined }),
        setIsRefreshingToken: (isRefreshing) => set({ isRefreshingToken: isRefreshing }),
        setTokens: (tokens) =>
          set({
            idToken: tokens.idToken,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            tokenExpiresAt: Date.now() + tokens.expiresIn * 1000,
            isAuthenticated: true,
            isLoading: false,
          }),
        clearTokens: () =>
          set({
            user: undefined,
            resellers: undefined,
            idToken: undefined,
            accessToken: undefined,
            refreshToken: undefined,
            tokenExpiresAt: undefined,
            isAuthenticated: false,
            isLoading: false,
            ability: ABILITIES.DEFAULT,
          }),
        setForceCountry: (country) => set({ forceCountry: country }),
      },
    }),
    {
      name: 'mobiway-app-store',
      partialize: (state) => ({
        sessionId: state.sessionId,
        idToken: state.idToken,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        forceCountry: state.forceCountry,
      }),
    }
  )
)

// Keep tabs in sync so a token rotated by one tab is never replayed by another.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'mobiway-app-store') {
      void useAppStore.persist.rehydrate()
    }
  })
}
